import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { InputGroupAddon } from '@/components/ui/input-group';
import { comboBoxOptionsOf, selectOption } from '@/components/ui/multi-select-utils';
import { coachesTypeaheadSearch, usePayCycleOptions } from '@/lib/api-call-hooks';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  createNonSessionPayment,
  getCoachNonSessionPayment,
  getCoachPayment,
  getOngoingPayments,
  getTotalSumFromLifetime,
  updateNonSessionPayment,
} from '@/lib/api-calls';
import { formatPlainDate } from '@airreading/common';
import { coachNonSessionPaymentKind, coachPaymentKindToLabel, PayCycleDTO } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { FromOptions } from './from-options';

const createPaymentSchema = z.object({
  coach: selectOption.optional(),
  payCycleInterval: z.string(),
  payDate: z.instanceof(Temporal.PlainDate),
  amount: z.coerce.number().min(0.01),
  type: coachNonSessionPaymentKind,
});

type CreatePaymentSubmitProps = SubmitProps<z.infer<typeof createPaymentSchema>>;

interface CreatePaymentProps {
  from: FromOptions;
  editId?: string;
  coach?: {
    id: string;
    fullNameOrEmail: string;
  };
  trigger: React.ReactNode;
}

interface PaymentForceSubmitProps extends CreatePaymentSubmitProps {
  force?: boolean;
}

export const ManualPaymentEntry = ({ from, editId, coach, trigger }: CreatePaymentProps) => {
  const { loggedInUser } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const payCycleOptions = usePayCycleOptions();
  const { data: coachNonSessionPayment, isLoading } = useQuery({
    ...getCoachNonSessionPayment({ nonSessionPaymentId: editId ?? '' }),
    enabled: !!editId,
  });

  const queryClient = useQueryClient();

  const { mutateAsync: createNonSessionPaymentMutation, isPending: createNonSessionPaymentPending } = useMutation({
    mutationFn: createNonSessionPayment,
  });

  const { mutateAsync: updateNonSessionPaymentMutation, isPending: updateNonSessionPaymentPending } = useMutation({
    mutationFn: updateNonSessionPayment,
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState<CreatePaymentSubmitProps | null>(null);

  const renderPayCycleOption = (value: string) => {
    const [start, end] = value.split('/');

    const startDate = formatPlainDate(Temporal.PlainDate.from(start));
    const endDate = formatPlainDate(Temporal.PlainDate.from(end));

    return `${startDate} - ${endDate}`;
  };

  const onSuccess = () => {
    if (from === FromOptions.teacherPay) {
      queryClient.invalidateQueries(getOngoingPayments());
      queryClient.invalidateQueries(getTotalSumFromLifetime());
    }
    if (from === FromOptions.payments) {
      queryClient.invalidateQueries(getCoachPayment());
      queryClient.invalidateQueries(getTotalSumFromLifetime());
    }
  };

  const onCreatePayment = async ({ formData, closeModal, reset, force }: PaymentForceSubmitProps) => {
    const { coach, payCycleInterval, payDate, amount, type } = formData;

    const payCycle = PayCycleDTO.fromIntervalString(payCycleInterval, payDate);
    try {
      await createNonSessionPaymentMutation({
        coachId: coach!.value,
        amount,
        type,
        payCycleId: payCycle.payPeriodId,
        createdById: loggedInUser.id,
        force,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.status === 409) {
          setPendingSubmit({ formData, closeModal, reset });
          setShowConfirm(true);
          return;
        }
      }
      toast({
        title: 'Payment creation failed',
        variant: 'destructive',
      });
      throw error;
    }
    toast({
      title: 'Payment created successfully',
      variant: 'default',
    });
    reset();
    navigate({
      to: location.pathname,
      search: () => ({}),
    });
  };

  const onUpdatePayment = async ({ formData }: CreatePaymentSubmitProps) => {
    const { amount, type, payCycleInterval, payDate } = formData;
    const payCycle = PayCycleDTO.fromIntervalString(payCycleInterval, payDate);

    try {
      await updateNonSessionPaymentMutation({
        id: editId!,
        data: {
          amount,
          type,
          payCycleId: payCycle.payPeriodId,
        },
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        description: airServerErrorFrom(error)?.userFacingMessage ?? 'Failed to update payment.',
      });
      throw error;
    }
    toast({
      title: 'Payment updated successfully',
      variant: 'default',
    });
  };

  const onMutate = async ({ formData, closeModal, reset, force }: PaymentForceSubmitProps) => {
    if (editId) {
      await onUpdatePayment({ formData, closeModal, reset });
    } else {
      await onCreatePayment({ formData, force, closeModal, reset });
    }
    reset();
    navigate({
      to: location.pathname,
      search: () => ({}),
    });
    onSuccess();
    closeModal();
  };

  const onSubmit = async ({ formData, closeModal, reset }: CreatePaymentSubmitProps) => {
    const { coach } = formData;

    if (!editId && !coach?.value) return;

    await onMutate({ formData, closeModal, reset });
  };

  const onConfirm = async () => {
    if (!pendingSubmit) return;

    setShowConfirm(false);
    await onMutate({ ...pendingSubmit, force: true });
  };

  const onCancel = () => {
    setShowConfirm(false);
    setPendingSubmit(null);
  };

  const onClose = () => {
    if (!editId) {
      return '';
    }
    navigate({
      to: location.pathname,
      search: () => ({}),
    });
  };

  const generateDefaultValues = (): DefaultValues<z.infer<typeof createPaymentSchema>> => {
    if (coach && !coachNonSessionPayment) {
      return {
        coach: {
          value: coach.id,
          label: coach.fullNameOrEmail,
        },
      };
    }

    if (!coachNonSessionPayment) return {};

    const payCycle = PayCycleDTO.fromPayPeriodId(coachNonSessionPayment.coachPayment?.payCycleId ?? '');
    const [cycle] = payCycle.payPeriodId.split('|');

    return {
      amount: coachNonSessionPayment.amountInCents ? coachNonSessionPayment.amountInCents / 100 : undefined,
      type: coachNonSessionPayment.type,
      payDate: payCycle.paymentPlainDate,
      payCycleInterval: cycle,
      coach: {
        value: coachNonSessionPayment.coachPayment?.coachId,
        label: coachNonSessionPayment.coachPayment?.coach?.fullNameOrEmail,
      },
    };
  };

  if (isLoading) return null;
  return (
    <>
      <FormModal
        key={editId}
        modalTitle={editId ? 'Update Payment' : 'Add Payment'}
        onSubmit={onSubmit}
        onClose={onClose}
        formSchema={createPaymentSchema}
        trigger={trigger}
        confirmBtnLabel={coachNonSessionPayment ? 'Save' : 'Add'}
        defaultValues={generateDefaultValues()}
        submitButtonProps={{
          loading: createNonSessionPaymentPending || updateNonSessionPaymentPending,
          disabled: createNonSessionPaymentPending || updateNonSessionPaymentPending,
        }}
        initialOpen={!!editId}
      >
        {({ form }) => {
          return (
            <>
              <div className="flex flex-col gap-4 py-4">
                <FormComboBox
                  form={form}
                  label={'Teacher'}
                  name={'coach'}
                  placeholder="Select an option"
                  {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, {}))}
                  renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
                  triggerProps={{
                    className: 'w-full',
                    disabled: !!coach,
                  }}
                  required
                />
                <FormSingleSelect
                  name="payCycleInterval"
                  label="Pay cycle"
                  form={form}
                  options={payCycleOptions.map((option) => option.value)}
                  renderOption={renderPayCycleOption}
                  selectProps={{
                    onValueChange: (value) => {
                      const defaultPaycycle = PayCycleDTO.fromIntervalString(value);
                      form.setValue('payCycleInterval', value);
                      form.setValue('payDate', defaultPaycycle.paymentPlainDate);
                    },
                    required: true,
                  }}
                  triggerProps={{
                    className: 'w-full',
                  }}
                />
                <FormInputDate
                  name="payDate"
                  label="Pay date"
                  form={form}
                  triggerButtonProps={{
                    className: 'w-full',
                  }}
                  required
                />
                <div className="grid grid-cols-2 items-center gap-4 w-full">
                  <FormSingleSelect
                    name="type"
                    label="Payment type"
                    form={form}
                    options={coachNonSessionPaymentKind.options}
                    renderOption={(value) => coachPaymentKindToLabel[value]}
                    selectProps={{
                      required: true,
                    }}
                  />

                  <FormInputField
                    name="amount"
                    label="Amount"
                    form={form}
                    inputProps={{
                      type: 'number',
                      className: 'w-full',
                      required: true,
                    }}
                    addons={<InputGroupAddon>$</InputGroupAddon>}
                  />
                </div>
              </div>
            </>
          );
        }}
      </FormModal>
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payment</DialogTitle>
            <DialogDescription>
              A payment for this pay cycle already exists. Are you sure you want to create another one?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={onConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
